import React, { useCallback, useEffect, useState } from "react";

import { useRole } from "./useRole";

import {
  Accordion,
  AccordionHeader,
  AccordionItem,
  AccordionPanel,
  AvatarGroup,
  AvatarGroupItem,
  AvatarGroupPopover,
  Button,
  Caption1Strong,
  Divider,
  DrawerBody,
  DrawerHeader,
  DrawerHeaderTitle,
  Field,
  OverlayDrawer,
  Persona,
  SearchBox,
  Select,
  Slider,
  Switch,
  makeStyles,
  partitionAvatarGroupItems,
  Dialog,
  DialogTrigger,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogActions,
  DialogContent,
  Checkbox
} from "@fluentui/react-components";
import {
  Dismiss24Regular,
  ChatMultiple20Filled,
  Mic20Filled,
  MicOff20Filled,
  Settings20Filled
} from "@fluentui/react-icons";
import { FormattedMessage } from "react-intl";
import { MAX_VOLUME_LABELS, calcLevel, calcGainMultiplier } from "../../utils/avatar-volume-utils";
import useAvatarVolume, { useAllAvatarVolume, useLocalAudio } from "./useAvatarVolume";
import { FluentNumericInput } from "../fluent/NumericInputCompound";
import useBreakpoints from "../hooks/useBreakpoints";
import configManager from "../../singleton/ConfigManager";

const MIN = 0;
const MAX = MAX_VOLUME_LABELS - 1;

const useStyles = makeStyles({
  drawer: {
    "& > *": {
      scrollbarWidth: "thin",
      "&::-webkit-scrollbar-button": {
        display: "none"
      },
      "&::-webkit-scrollbar-button:vertical:decrement, &::-webkit-scrollbar-button:vertical:increment": {
        display: "none", // Specifically hide the up/down buttons
        height: "0" // Ensure no space is left for the button
      }
    }
  },
  headerDrawer: {
    display: " flex",
    padding: " var(--Font-size-600, 24px) 16px 12px var(--Font-size-600, 24px)",
    alignItems: " flex-start",
    gap: " 8px",
    alignSelf: " stretch",
    backgroundColor: "#16181E"
  },
  drawerBody: {},
  search: {
    backgroundColor: "rgba(255, 255, 255, 0.10)",
    width: "100%"
  },
  section: {
    display: " flex",
    flexDirection: " column",
    alignItems: " flex-start",
    gap: "2px"
  },
  sectionHeader: {
    display: " flex",
    padding: " 8px 0px",
    flexDirection: " column",
    alignItems: " flex-start",
    gap: "var(--Font-size-100, 10px)",
    alignSelf: "stretch"
  },
  sectionHeaderText: {
    color: "var(--Gray-3, #828282)"
  },
  sectionContent: {
    width: "100%",
    display: " flex",
    padding: " 4px",
    alignItems: " center",
    height: "48px",
    borderRadius: "4px",
    justifyContent: "space-between",
    gap: " 8px",
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.1)"
    }
  },
  persona: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "32px",
    "& .fui-Avatar__image": {
      width: "32px",
      height: "32px"
    },
    "& .fui-Avatar": {
      height: "32px"
    },
    "& .fui-Persona": {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      height: "32px"
    }
  },
  icons: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    "& > *": {
      cursor: "pointer"
    }
  },
  divider: {
    padding: "5px 0px 4px 0px"
  },
  contentParticipants: {},
  footerParticipants: {
    display: "flex",
    padding: "16px var(--Font-size-600, 24px) var(--Font-size-600, 24px) var(--Font-size-600, 24px)",
    width: "100%",
    backgroundColor: "#16181E"
  },
  redIcon: {
    color: "var(--Red, #EB5757)"
  },
  accordionPanel: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    marginRight: "0px!important",
    "&:last-child": {
      marginBottom: "8px"
    },
    "& .fui-Field": {
      gridTemplateColumns: "1fr 1fr",
      "& .fui-Switch": {
        justifyContent: "flex-end"
      }
    }
  },
  dialogContent: {
    display: " flex",
    padding: " var(--Vertical-None, 0px) var(--Horizontal-None, 0px)",
    flexDirection: " column",
    alignItems: " flex-start",
    gap: " var(--Vertical-SNudge, 6px)",
    alignSelf: " stretch",
    overflow: "hidden"
  },
  volumeContainer: {
    display: "flex",
    gap: "8px"
  },
  compoundNumericInput: {
    minWidth: "60px",
    backgroundColor: "rgba(245, 245, 245, 0.10)"
  }
});

export default function FluentSettingParticipantOverlay({
  people,
  hubChannel,
  isOpen,
  onMute,
  onMuteAll,
  person,
  allMuted,
  isAllHidden,
  allHost,
  onClose,
  onChange
}) {
  const { id: userId, profile, roles } = person || {};

  const styles = useStyles();

  const [isOwner, setIsOwner] = useState(!!roles?.owner);
  const isCreator = !!roles?.creator;
  const isSignedIn = !!roles?.signed_in;

  // Old Functionalities commented to use it when is needed
  // const hasMicPresence = !!person?.micPresence;
  // const isNetworkMuted = person?.micPresence?.muted;
  // const mayMute = !isNetworkMuted && hubChannel.canOrWillIfCreator("mute_users");
  const mayKick = hubChannel.canOrWillIfCreator("kick_users");
  // const mayAddOwner = hubChannel.canOrWillIfCreator("update_roles") && !isOwner && !isCreator;
  // const mayRemoveOwner = hubChannel.canOrWillIfCreator("update_roles") && isOwner && !isCreator;
  const [isHidden, setIsHidden] = useState(hubChannel.isHidden(person?.id));

  useEffect(() => {
    setIsOwner(!!roles?.owner);
  }, [person]);

  const onToggleHost = async () => {
    if (!isOwner) {
      await hubChannel.addOwner(userId);
      setIsOwner(true);
    } else {
      await hubChannel.removeOwner(userId);
      setIsOwner(false);
    }
  };

  const onToggleAllHost = async allHost => {
    for (const person of people) {
      if (!person.isMe) {
        if (!allHost) {
          await hubChannel.addOwner(person.id);
        } else {
          await hubChannel.removeOwner(person.id);
        }
      }
    }
  };

  const toggleHidden = useCallback(() => {
    if (isHidden) {
      hubChannel.unhide(userId);
    } else {
      hubChannel.hide(userId);
    }

    setIsHidden(!isHidden);
  }, [isHidden, userId, hubChannel]);

  const kick = useCallback(async () => {
    await hubChannel.kick(userId);
    onClose();
  }, [hubChannel, userId]);

  const kickAll = useCallback(async () => {
    for (const person of people) {
      if (!person.isMe && !person.roles.creator) {
        await hubChannel.kick(person.id);
      }
    }
    onClose();
  }, [hubChannel, people]);

  const hideAll = useCallback(async () => {
    for (const person of people) {
      if (hubChannel.isHidden(person?.id) && !person?.isMe) {
        hubChannel.unhide(person?.id);
      } else if (!person?.isMe && !person?.roles?.creator) {
        hubChannel.hide(person?.id);
      }
    }
  }, [hubChannel, people]);

  const canUpdateRoles = hubChannel.canOrWillIfCreator("update_roles");
  const breakpoint = useBreakpoints();
  const isMobile = breakpoint === "sm" || breakpoint === "xs";

  const overlayStyle = isMobile ? {} : { width: "400px" };

  return (
    <OverlayDrawer
      className={styles.drawer}
      modalType="non-modal"
      open={isOpen}
      position="end"
      size="full"
      style={overlayStyle}
    >
      <DrawerContent
        mayKick={mayKick}
        styles={styles}
        onClose={onClose}
        onMuteAll={onMuteAll}
        headerName={profile?.displayName || "Everyone"}
        onToggleHost={onToggleHost}
        isOwner={isOwner}
        isCreator={isCreator}
        userId={userId}
        person={person}
        onMute={onMute}
        onToggleHide={toggleHidden}
        isHidden={isHidden}
        kick={kick}
        allMuted={allMuted}
        kickAll={kickAll}
        isAllHidden={isAllHidden}
        hideAll={hideAll}
        allHost={allHost}
        onToggleAllHost={onToggleAllHost}
        people={people}
        canUpdateRoles={canUpdateRoles}
      />
    </OverlayDrawer>
  );
}

function DrawerContent({
  styles,
  onClose,
  onMuteAll,
  headerName,
  onToggleHost,
  isOwner,
  isCreator,
  userId,
  person,
  people,
  onMute,
  onToggleHide,
  isHidden,
  mayKick,
  kick,
  allMuted,
  kickAll,
  hideAll,
  isAllHidden,
  allHost,
  onToggleAllHost,
  canUpdateRoles
}) {
  const [maxVolume, setMaxVolume] = useState(MAX / 2);
  const { multipliers, updatePersonMultiplier } = useAllAvatarVolume(people);
  const voiceChatEnabled = configManager.config.voiceChat.enabled;
  const [localVoice, updateLocalAudio] = useLocalAudio();

  const onLevelChanged = useCallback(
    level => {
      const newMultiplier = calcGainMultiplier(level);
      if (person) {
        updatePersonMultiplier(person.id, newMultiplier);
      } else {
        people.forEach(p => {
          if (p.isMe) {
            updateLocalAudio(level * 10);
          } else {
            updatePersonMultiplier(p.id, newMultiplier);
          }
        });
        setMaxVolume(level);
      }
    },
    [updatePersonMultiplier, person, people]
  );

  const newLevel = person ? calcLevel(multipliers[person.id]) || 0 : maxVolume;
  const hostDisabled =
    people.length === 1 ||
    (person ? person?.isMe || isCreator || !person?.roles?.signed_in : allHost) ||
    !canUpdateRoles;

  const isMod = useRole("owner");

  const isAll = !person;

  const hideDisabled = (person && person?.isMe) || (isAll && !isMod);
  const expelDisabled = (person && person?.isMe) || isCreator || (isAll && !isMod);
  const showAudio = person ? person?.presence !== "lobby" : true;
  const disableMicrophone =
    (person && !person?.isMe && multipliers[person?.id] === undefined) ||
    person?.micPresence?.muted ||
    allMuted ||
    isHidden || !voiceChatEnabled;
  const disableVolume =
    (person && multipliers[person?.id] === undefined) || person?.micPresence?.muted || allMuted || isHidden;

  return (
    <>
      <DrawerHeader className={styles.headerDrawer}>
        <DrawerHeaderTitle
          style={{ width: "100%" }}
          action={
            <Button appearance="subtle" aria-label="Close" icon={<Dismiss24Regular />} onClick={() => onClose()} />
          }
        >
          {headerName}
        </DrawerHeaderTitle>
      </DrawerHeader>

      <DrawerBody className={styles.drawerBody}>
        <Accordion collapsible multiple defaultOpenItems={["1", "2", "3"]}>
          <AccordionItem value="1">
            <AccordionHeader className={styles.accordionHeader}>
              <span className={styles.accordionHeaderText}>Roles & Permissions</span>
            </AccordionHeader>
            <AccordionPanel className={styles.accordionPanel}>
              <Field label={"Host"} orientation="horizontal">
                <Switch
                  disabled={hostDisabled}
                  onClick={() => (person ? onToggleHost() : onToggleAllHost(allHost))}
                  checked={person ? isOwner : allHost}
                />
              </Field>
            </AccordionPanel>
          </AccordionItem>
          <Divider />
          {showAudio && (
            <AccordionItem value="2">
              <AccordionHeader className={styles.accordionHeader}>
                <span className={styles.accordionHeaderText}>Audio</span>
              </AccordionHeader>
              <AccordionPanel className={styles.accordionPanel}>
                <Field label={"Volume"} orientation="horizontal">
                  {!person?.isMe && (
                    <div className={styles.volumeContainer}>
                      <FluentNumericInput
                        value={person ? newLevel : maxVolume}
                        min={MIN}
                        max={MAX}
                        onChange={value => onLevelChanged(value)}
                        className={styles.compoundNumericInput}
                        disabled={disableVolume}
                      />
                      <Slider
                        min={MIN}
                        max={MAX}
                        value={person ? newLevel : maxVolume}
                        onChange={(ev, data) => onLevelChanged(data.value)}
                        disabled={disableVolume}
                      />
                    </div>
                  )}
                  {person?.isMe && (
                    <div className={styles.volumeContainer}>
                      <FluentNumericInput
                        value={Math.floor(localVoice / 10)}
                        min={MIN}
                        max={MAX}
                        onChange={value => updateLocalAudio(value * 10)}
                        className={styles.compoundNumericInput}
                        disabled={person?.micPresence?.muted || allMuted}
                      />

                      <Slider
                        min={MIN}
                        max={200}
                        value={localVoice}
                        onChange={(ev, data) => updateLocalAudio(data.value)}
                        disabled={person?.micPresence?.muted || allMuted}
                      />
                    </div>
                  )}
                </Field>
                <Field label={"Microphone"} orientation="horizontal">
                  <Switch
                    checked={person ? person?.micPresence?.muted == false : !allMuted}
                    disabled={disableMicrophone}
                    onClick={() => (person ? onMute(person) : onMuteAll())}
                  />
                </Field>
              </AccordionPanel>
            </AccordionItem>
          )}
          <Divider />
        </Accordion>
      </DrawerBody>
      <div className={styles.footerParticipants} style={{ justifyContent: person ? "space-between" : "space-between" }}>
        {/* <Button appearance="suble">Invite</Button> */}
        <Button appearance="suble" disabled={hideDisabled} onClick={person ? onToggleHide : hideAll}>
          {person ? (isHidden ? "Unhide" : "Hide") : isAllHidden ? "Unhide" : "Hide"}
        </Button>
        <ExpelModal person={person} styles={styles} kick={kick} kickAll={kickAll}>
          <Button disabled={expelDisabled} appearance="suble">
            Expel
          </Button>
        </ExpelModal>
      </div>
    </>
  );
}

function ExpelModal({ children, styles, kick, kickAll, person }) {
  return (
    <Dialog>
      <DialogTrigger>{children}</DialogTrigger>
      <DialogSurface style={{ maxWidth: "480px" }}>
        <DialogBody>
          <DialogTitle
            action={
              <DialogTrigger>
                <Button appearance="transparent" icon={<Dismiss24Regular />} />
              </DialogTrigger>
            }
          >
            {person ? "Expel User" : "Expel All Users"}
          </DialogTitle>
          <DialogContent className={styles.dialogContent}>
            <div>
              {person
                ? `Do you want to Expel ${person?.profile?.displayName} from the verse?`
                : "Do you want to Expel everyone from the verse?"}
            </div>
            <div>
              <Checkbox label={"Complete an abuse report"} />
            </div>
            <div>
              <Checkbox label={"Lock in this Verse"} />
            </div>
          </DialogContent>
          <DialogActions>
            <DialogTrigger disableButtonEnhancement>
              <Button appearance="secondary">Cancel</Button>
            </DialogTrigger>
            <Button onClick={person ? kick : kickAll} appearance="primary">
              Expel
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
